import React from 'react';
import { Table } from 'antd';
import MuiInputNumber from '@shared/components/MuiInputNumber';
import cx from 'classnames';
import {
  CERTIFICATION_DOMAIN,
  CERTIFICATION_DOMAIN_EXPERIENCE_ROLE
} from '../constants/certificationDomain';

/**
 * @typedef {object} ExperienceTableProps
 * @property {Object} [appraiserData]
 * @property {(domain: number, role: number, newValue: number | string) => void} [setValue]
 * @property {boolean} [showAtmAtlRows]
 * @property {boolean} [disableEditAscDomain]
 * @property {boolean} [hideAscDomainColumn]
 */

/**
 * @param {ExperienceTableProps} props
 */
const ExperienceTable = props => {
  const {
    setValue,
    appraiserData = {},
    showAtmAtlRows = false,
    hideAscDomainColumn = false,
    disableEditAscDomain = false
  } = props;

  const columns = [
    {
      title: '',
      dataIndex: 'title',
      render: text => text,
      key: 'title',
      width: '10%'
    },
    {
      title: 'ASC',
      dataIndex: 'ascDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.ASC, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.ASC, record),
      hidden: hideAscDomainColumn,
      width: '10%'
    },
    {
      title: 'DATA',
      dataIndex: 'dataDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.DATA, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.DATA, record)
    },
    {
      title: 'DEV',
      dataIndex: 'developmentDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.DEV, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.DEV, record)
    },
    {
      title: 'HM',
      dataIndex: 'highMaturityDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.HM, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.HM, record)
    },
    {
      title: 'PPL',
      dataIndex: 'peopleDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.PPL, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.PPL, record)
    },
    {
      title: 'SAF',
      dataIndex: 'safetyDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.SAF, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.SAF, record)
    },
    {
      title: 'SEC',
      dataIndex: 'securityDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.SEC, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.SEC, record)
    },
    {
      title: 'SVC',
      dataIndex: 'serviceDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.SVC, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.SVC, record)
    },
    {
      title: 'SPM',
      dataIndex: 'suppliersDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.SPM, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.SPM, record)
    },
    {
      title: 'VRT',
      dataIndex: 'virtualDomainExperience',
      render: (text, record) => renderCell(CERTIFICATION_DOMAIN.VRT, text, record),
      onCell: record => setCellClassName(CERTIFICATION_DOMAIN.VRT, record)
    }
  ].filter(item => !item.hidden);

  const getDomainValue = (role, domain) => {
    if (!appraiserData.domainExperiences) {
      return;
    }

    const experience = appraiserData.domainExperiences.find(
      experience => experience.role === role && experience.domain === domain
    );
    return experience ? experience.yearsOfExperience : 0;
  };

  const tableData = [
    {
      key: CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
      title: 'ATM',
      ascDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.ASC
      ),
      dataDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.DATA
      ),
      developmentDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.DEV
      ),
      highMaturityDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.HM
      ),
      peopleDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.PPL
      ),
      safetyDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.SAF
      ),
      securityDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.SEC
      ),
      serviceDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.SVC
      ),
      suppliersDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.SPM
      ),
      virtualDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM,
        CERTIFICATION_DOMAIN.VRT
      )
    },
    {
      key: CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
      title: 'ATL',
      ascDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.ASC
      ),
      dataDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.DATA
      ),
      developmentDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.DEV
      ),
      highMaturityDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.HM
      ),
      peopleDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.PPL
      ),
      safetyDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.SAF
      ),
      securityDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.SEC
      ),
      serviceDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.SVC
      ),
      suppliersDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.SPM
      ),
      virtualDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL,
        CERTIFICATION_DOMAIN.VRT
      )
    },
    {
      key: CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
      title: 'Experience (Years)',
      ascDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.ASC
      ),
      dataDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.DATA
      ),
      developmentDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.DEV
      ),
      highMaturityDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.HM
      ),
      peopleDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.PPL
      ),
      safetyDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.SAF
      ),
      securityDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.SEC
      ),
      serviceDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.SVC
      ),
      suppliersDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.SPM
      ),
      virtualDomainExperience: getDomainValue(
        CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL,
        CERTIFICATION_DOMAIN.VRT
      )
    }
  ];

  const getTableData = () =>
    showAtmAtlRows
      ? tableData
      : tableData.filter(x => x.key === CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL);

  const setCellClassName = (domain, record) => {
    const isAtlCell = record.key === CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL;
    const isAtmCell = record.key === CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM;
    const isTotalRole = record.key === CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.TOTAL;
    const isAscDomain = domain === CERTIFICATION_DOMAIN.ASC;
    const hasCert =
      appraiserData.domainExperiences &&
      appraiserData.domainExperiences.some(
        x => x.domain === domain && x.role === record.key && x.hasDomainAccess
      );

    return {
      className: cx('', {
        'atl-cell': isAtlCell,
        'atm-cell': isAtmCell,
        'cert-expired': !isTotalRole && !hasCert,
        'asc-input': !isTotalRole && !disableEditAscDomain && isAscDomain
      })
    };
  };

  const renderCell = (domain, value, record) => {
    const isAscDomain = domain === CERTIFICATION_DOMAIN.ASC;

    if (isAscDomain && !disableEditAscDomain) {
      return renderNumberInput(value, domain, record.key);
    }

    return value;
  };

  const renderNumberInput = (value, domain, role) => {
    return (
      <MuiInputNumber
        value={value}
        handleChange={newValue => {
          setValue(domain, role, newValue);
        }}
      />
    );
  };

  return (
    <Table
      className='exp-info-table'
      columns={columns}
      dataSource={getTableData()}
      pagination={false}
      showSorterTooltip={false}
      rowClassName={record => TableRowClassNames[record.key] || ''}
      onRow={(_, rowIndex) => ({
        'data-testid': `row-${rowIndex}`
      })}
      style={{ width: '100%' }}
    />
  );
};

const TableRowClassNames = {
  [CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATM]: 'atm-row',
  [CERTIFICATION_DOMAIN_EXPERIENCE_ROLE.ATL]: 'atl-row'
};

export default ExperienceTable;
